import { useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UtrInput, isUtr } from "@/components/UtrInput";
import { Field } from "@/pages/user/UserPayouts";
import { api, errMsg, fileUrl } from "@/lib/api";

export const Card = ({ title, desc, children, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid={testid}>
    <h3 className="font-display text-base font-semibold text-[#0A1F44]">{title}</h3>
    {desc && <p className="mt-1 text-xs text-slate-500">{desc}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

const Btn = ({ busy, children, testid, tone = "bg-[#2563EB] hover:bg-[#1D4ED8]" }) => (
  <button data-testid={testid} disabled={busy} className={`flex h-10 w-full items-center justify-center gap-2 rounded-md text-sm font-semibold text-white transition-colors disabled:opacity-60 ${tone}`}>
    {busy && <Loader2 className="h-4 w-4 animate-spin" />} {children}
  </button>
);

const useSubmit = (fn) => {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await fn(); } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return [busy, run];
};

const Sel = ({ value, onChange, items, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testid}-${v}`}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export const WalletAdjustForm = ({ uid, onDone }) => {
  const [f, setF] = useState({ wallet: "collection", type: "credit", amount: "", utr: "", note: "" });
  const [busy, run] = useSubmit(async () => {
    if (!isUtr(f.utr)) throw { message: "UTR must be exactly 12 digits" };
    await api.post(`/admin/users/${uid}/wallet`, { ...f, amount: Number(f.amount) });
    toast.success(`${f.wallet} wallet ${f.type}ed`); setF({ ...f, amount: "", utr: "", note: "" }); onDone();
  });
  return (
    <Card title="Update wallet (UTR)" desc="Credit customer QR payments or chargebacks with 12-digit UTR" testid="wallet-adjust-card">
      <form onSubmit={run} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Sel testid="adjust-wallet-select" value={f.wallet} onChange={(v) => setF({ ...f, wallet: v })} items={[["collection", "Collection"], ["chargeback", "Chargeback"], ["payout", "Payout"]]} />
          <Sel testid="adjust-type-select" value={f.type} onChange={(v) => setF({ ...f, type: v })} items={[["credit", "Credit (+)"], ["debit", "Debit (-)"]]} />
        </div>
        <Input data-testid="adjust-amount-input" required type="number" min="0.01" step="0.01" placeholder="Amount (₹)" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
        <UtrInput testid="adjust-utr-input" value={f.utr} onChange={(v) => setF({ ...f, utr: v })} />
        <Input data-testid="adjust-note-input" placeholder="Note (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        <Btn busy={busy} testid="adjust-submit-btn">Update wallet</Btn>
      </form>
    </Card>
  );
};

export const TransferForm = ({ uid, onDone }) => {
  const [f, setF] = useState({ direction: "collection_to_payout", amount: "", note: "" });
  const [busy, run] = useSubmit(async () => {
    await api.post(`/admin/users/${uid}/transfer`, { ...f, amount: Number(f.amount) });
    toast.success("Funds transferred"); setF({ ...f, amount: "", note: "" }); onDone();
  });
  return (
    <Card title="Wallet transfer" desc="Move funds between Collection and Payout wallets" testid="transfer-card">
      <form onSubmit={run} className="space-y-3">
        <Sel testid="transfer-direction-select" value={f.direction} onChange={(v) => setF({ ...f, direction: v })} items={[["collection_to_payout", "Collection → Payout"], ["payout_to_collection", "Payout → Collection"]]} />
        <Input data-testid="transfer-amount-input" required type="number" min="0.01" step="0.01" placeholder="Amount (₹)" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
        <Input data-testid="transfer-note-input" placeholder="Note (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        <Btn busy={busy} testid="transfer-submit-btn" tone="bg-[#0A1F44] hover:bg-[#13306a]">Transfer funds</Btn>
      </form>
    </Card>
  );
};

export const QrUpiForm = ({ user, onDone }) => {
  const [upi, setUpi] = useState(user.upi_id || "");
  const [file, setFile] = useState(null);
  const [busy, run] = useSubmit(async () => {
    if (upi !== (user.upi_id || "")) await api.put(`/admin/users/${user.id}`, { upi_id: upi });
    if (file) {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/users/${user.id}/qr`, fd);
      setFile(null);
    }
    toast.success("Static QR / UPI updated"); onDone();
  });
  return (
    <Card title="Static QR & UPI ID" desc="Upload the QR image the merchant's customers will scan" testid="qr-upi-card">
      <form onSubmit={run} className="flex flex-col gap-4 sm:flex-row">
        <div className="grid h-32 w-32 shrink-0 place-items-center overflow-hidden rounded-lg border border-dashed border-slate-300 bg-slate-50">
          {file ? <img src={URL.createObjectURL(file)} alt="preview" className="h-full w-full object-contain" />
            : user.qr_url ? <img src={fileUrl(user.qr_url)} alt="QR" className="h-full w-full object-contain" data-testid="admin-qr-preview" />
            : <span className="text-xs text-slate-400">No QR</span>}
        </div>
        <div className="flex-1 space-y-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
            <Upload className="h-4 w-4" /> <span className="truncate">{file ? file.name : "Select QR image"}</span>
            <input data-testid="qr-file-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <Input data-testid="upi-id-input" placeholder="merchant@upi" value={upi} onChange={(e) => setUpi(e.target.value)} />
          <Btn busy={busy} testid="qr-upi-save-btn">Save QR & UPI</Btn>
        </div>
      </form>
    </Card>
  );
};

export const EditProfileDialog = ({ user, open, setOpen, onDone }) => {
  const [f, setF] = useState({ name: user.name, business_name: user.business_name || "", email: user.email, username: user.username, phone: user.phone || "" });
  const [busy, run] = useSubmit(async () => {
    await api.put(`/admin/users/${user.id}`, f);
    toast.success("Profile updated"); setOpen(false); onDone();
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="edit-profile-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit profile</DialogTitle></DialogHeader>
        <form onSubmit={run} className="grid gap-3 sm:grid-cols-2">
          {[["name", "Full name"], ["business_name", "Business name"], ["email", "Email"], ["username", "Username"], ["phone", "Phone"]].map(([k, l]) => (
            <Field key={k} label={l}><Input data-testid={`edit-${k}-input`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></Field>
          ))}
          <div className="sm:col-span-2"><Btn busy={busy} testid="edit-profile-submit-btn">Save changes</Btn></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const PasswordDialog = ({ uid, open, setOpen }) => {
  const [pw, setPw] = useState("");
  const [busy, run] = useSubmit(async () => {
    await api.post(`/admin/users/${uid}/password`, { password: pw });
    toast.success("Password changed"); setPw(""); setOpen(false);
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="password-dialog">
        <DialogHeader><DialogTitle className="font-display">Change password</DialogTitle></DialogHeader>
        <form onSubmit={run} className="space-y-3">
          <Input data-testid="new-password-input" type="text" minLength={6} required placeholder="New password (min 6 chars)" value={pw} onChange={(e) => setPw(e.target.value)} />
          <Btn busy={busy} testid="password-submit-btn">Update password</Btn>
        </form>
      </DialogContent>
    </Dialog>
  );
};
